/* Video Reels — <video-reels> center-focus (coverflow) carousel.
 * The active reel is centered, enlarged and sharp; side reels scale down and
 * blur. Infinite loop (with teleport so wrapped cards don't sweep across).
 * Only the active reel plays (muted autoplay while the section is in view).
 * Navigate by arrows, drag/swipe, horizontal wheel, or arrow keys. Honors
 * reduced-motion and pauses when the tab is hidden. See video-reels.liquid. */
(function () {
  'use strict';

  class VideoReels extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('[data-reels-stage]');
      this.reels = Array.prototype.slice.call(this.querySelectorAll('[data-reel]'));
      if (!this.stage || !this.reels.length) return;

      this.autoplay = this.dataset.autoplay === 'true';
      this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.active = 0;
      this.soundOn = false;
      this.sectionVisible = true;
      this.maxVisible = 2;
      this.step = 200;
      this._prevD = {};

      this.reels.forEach(this.initReel.bind(this));
      this.initNav();
      this.initPointer();
      this.initWheel();
      this.initKeys();

      // First paint: position instantly (no entrance sweep), then enable motion.
      this.reels.forEach(function (c) { c.style.transition = 'none'; });
      this.classList.add('is-ready');
      this.measure();
      this.render();
      var self = this;
      requestAnimationFrame(function () {
        self.reels.forEach(function (c) { c.style.transition = ''; });
      });

      if ('ResizeObserver' in window) {
        this._ro = new ResizeObserver(function () { self.measure(); self.render(); });
        this._ro.observe(this);
      }
      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver(function (entries) {
          self.sectionVisible = entries[0].isIntersecting;
          self.updatePlayback();
        }, { threshold: 0.25 });
        this._io.observe(this);
      }
      this._onVis = function () { if (document.hidden) self.pauseAll(); else self.updatePlayback(); };
      document.addEventListener('visibilitychange', this._onVis);
    }

    disconnectedCallback() {
      document.removeEventListener('visibilitychange', this._onVis);
      if (this._ro) this._ro.disconnect();
      if (this._io) this._io.disconnect();
    }

    initReel(reel) {
      var video = reel.querySelector('.reel__video');
      if (!video) return;
      reel._video = video;
      var self = this;
      var playBtn = reel.querySelector('[data-reel-play]');
      var muteBtn = reel.querySelector('[data-reel-mute]');

      var setPlaying = function (p) {
        reel.dataset.playing = p ? 'true' : 'false';
        if (playBtn) {
          playBtn.setAttribute('aria-pressed', p ? 'true' : 'false');
          playBtn.setAttribute('aria-label', p ? 'Pausa' : 'Riproduci');
        }
      };
      setPlaying(false);
      video.addEventListener('play', function () { setPlaying(true); });
      video.addEventListener('pause', function () { setPlaying(false); });

      // Paused side reels have no poster frame until played; nudge to the first
      // frame so they read as (blurred) video, not empty cards.
      video.addEventListener('loadedmetadata', function () {
        if (video.paused && video.currentTime < 0.01) {
          try { video.currentTime = 0.05; } catch (err) {}
        }
      });

      if (playBtn) playBtn.addEventListener('click', function (e) { e.stopPropagation(); self.togglePlay(reel); });
      if (muteBtn) muteBtn.addEventListener('click', function (e) { e.stopPropagation(); self.toggleSound(); });
    }

    measure() {
      var card = this.reels[0];
      var base = card ? card.offsetWidth : 280;
      var vw = this.stage.clientWidth || window.innerWidth;
      // spacing between adjacent card centers: side reels peek beside the
      // enlarged center; pull them closer on narrow viewports.
      this.step = Math.min(base * 0.82, vw * 0.42);
    }

    render() {
      var N = this.reels.length;
      for (var i = 0; i < N; i++) {
        var card = this.reels[i];
        var d = i - this.active;
        if (d > N / 2) d -= N;
        if (d < -N / 2) d += N;
        var ad = Math.abs(d);
        var beyond = ad > this.maxVisible;
        var op = this.opacityFor(ad, beyond);

        var prev = this._prevD[i];
        // A normal step shifts a card by exactly one slot; a larger jump means
        // the card wrapped around the loop's seam.
        var wrapped = prev !== undefined && Math.abs(d - prev) > 1.5;

        if (wrapped && !this.reduce) {
          this.wrapCard(card, d, ad, beyond, op);
        } else if (wrapped) {
          card.style.transition = 'none';
          this.positionCard(card, d, ad, beyond);
          card.style.opacity = String(op);
          void card.offsetWidth;
          card.style.transition = '';
        } else {
          if (card._wrapTimer) { clearTimeout(card._wrapTimer); card._wrapTimer = null; card.style.transition = ''; }
          this.positionCard(card, d, ad, beyond);
          card.style.opacity = String(op);
        }
        this._prevD[i] = d;

        var isActive = d === 0;
        card.classList.toggle('is-active', isActive);
        card.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        var focusables = card.querySelectorAll('a, button');
        for (var f = 0; f < focusables.length; f++) focusables[f].tabIndex = isActive ? 0 : -1;
      }
      this.updateSoundUI();
      this.updatePlayback();
    }

    opacityFor(ad, beyond) {
      return beyond ? 0 : ad === 0 ? 1 : ad === 1 ? 0.72 : 0.42;
    }

    positionCard(card, d, ad, beyond) {
      var s = beyond ? 0.55 : Math.pow(0.8, ad);
      var blur = ad === 0 ? 0 : Math.min(ad * 5, 12);
      var off = beyond ? (d < 0 ? -1 : 1) * (this.maxVisible + 1) * this.step : d * this.step;
      card.style.transform = 'translate(-50%, -50%) translateX(' + off + 'px) scale(' + s + ')';
      card.style.filter = blur ? 'blur(' + blur + 'px)' : 'none';
      card.style.zIndex = String(100 - ad);
      card.style.pointerEvents = beyond ? 'none' : 'auto';
    }

    // Graceful wrap: fade the card out where it is, teleport it across the seam
    // while invisible, then fade it back in at the opposite edge — no pop, no
    // sweep through the center.
    wrapCard(card, d, ad, beyond, op) {
      var self = this;
      if (card._wrapTimer) clearTimeout(card._wrapTimer);
      card.style.transition = 'opacity 0.26s ease';
      card.style.opacity = '0';
      card._wrapTimer = setTimeout(function () {
        card._wrapTimer = null;
        card.style.transition = 'none';
        self.positionCard(card, d, ad, beyond);
        card.style.opacity = '0';
        void card.offsetWidth;
        card.style.transition = '';
        card.style.opacity = String(op);
      }, 260);
    }

    go(idx) {
      var N = this.reels.length;
      this.active = ((idx % N) + N) % N;
      var a = this.reels[this.active];
      if (a) a._userPaused = false;
      this.render();
    }
    next() { this.go(this.active + 1); }
    prev() { this.go(this.active - 1); }

    togglePlay(reel) {
      var v = reel._video;
      if (!v) return;
      if (v.paused) { reel._userPaused = false; this.playVideo(reel); }
      else { reel._userPaused = true; v.pause(); }
    }

    playVideo(reel) {
      var v = reel._video;
      if (!v) return;
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
    }

    toggleSound() {
      this.soundOn = !this.soundOn;
      this.updateSoundUI();
      var a = this.reels[this.active];
      if (a && a._video) {
        a._video.muted = !this.soundOn;
        if (this.soundOn) { a._userPaused = false; this.playVideo(a); }
      }
    }

    updateSoundUI() {
      for (var i = 0; i < this.reels.length; i++) {
        var btn = this.reels[i].querySelector('[data-reel-mute]');
        if (!btn) continue;
        var on = i === this.active && this.soundOn;
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        btn.setAttribute('aria-label', on ? 'Disattiva audio' : 'Attiva audio');
      }
    }

    updatePlayback() {
      for (var i = 0; i < this.reels.length; i++) {
        var card = this.reels[i];
        var v = card._video;
        if (!v) continue;
        if (i === this.active) {
          v.muted = !this.soundOn;
          if (this.autoplay && !this.reduce && this.sectionVisible && !document.hidden && !card._userPaused) {
            this.playVideo(card);
          }
        } else {
          v.pause();
          v.muted = true;
        }
      }
    }

    pauseAll() {
      this.reels.forEach(function (r) { if (r._video) r._video.pause(); });
    }

    initNav() {
      var self = this;
      var p = this.querySelector('[data-reels-prev]');
      var n = this.querySelector('[data-reels-next]');
      if (p) p.addEventListener('click', function () { self.prev(); });
      if (n) n.addEventListener('click', function () { self.next(); });
    }

    initPointer() {
      var self = this;
      var st = this.stage;
      var startX = 0, down = false, moved = false;

      var onMove = function (e) {
        if (!down) return;
        if (!moved && Math.abs(e.clientX - startX) > 8) { moved = true; st.classList.add('is-dragging'); }
      };
      var onUp = function (e) {
        if (!down) return;
        down = false;
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        st.classList.remove('is-dragging');
        if (moved) {
          var dx = e.clientX - startX;
          if (Math.abs(dx) > 40) { if (dx < 0) self.next(); else self.prev(); }
          // a drag also fires a click on release; swallow that one tap-nav
          self._suppressClick = true;
          setTimeout(function () { self._suppressClick = false; }, 60);
        }
      };

      st.addEventListener('pointerdown', function (e) {
        if (e.button && e.button !== 0) return;
        startX = e.clientX; down = true; moved = false;
        // Track on window (drag can leave the stage) and do NOT capture the
        // pointer — capturing would stop the reels' buttons/links from getting
        // their native click.
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
      });

      // Tap navigation via click, so the buttons (play/mute) and CTA links keep
      // their own native click handlers.
      st.addEventListener('click', function (e) {
        if (self._suppressClick) return;
        if (e.target.closest('[data-reel-play], [data-reel-mute], .reel__cta')) return;
        var card = e.target.closest('[data-reel]');
        if (!card) return;
        var idx = self.reels.indexOf(card);
        if (idx === self.active) self.togglePlay(card);
        else self.go(idx);
      });
    }

    initWheel() {
      var self = this;
      var acc = 0, locked = false;
      var vp = this.querySelector('.reels__viewport');
      if (!vp) return;
      vp.addEventListener('wheel', function (e) {
        var horiz = Math.abs(e.deltaX) > Math.abs(e.deltaY);
        var delta = horiz ? e.deltaX : (e.shiftKey ? e.deltaY : 0);
        if (!delta) return;
        e.preventDefault();
        acc += delta;
        if (locked) return;
        if (Math.abs(acc) > 40) {
          if (acc > 0) self.next(); else self.prev();
          acc = 0; locked = true;
          setTimeout(function () { locked = false; }, 420);
        }
      }, { passive: false });
    }

    initKeys() {
      var self = this;
      this.stage.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); self.prev(); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); self.next(); }
      });
    }
  }

  if (!customElements.get('video-reels')) customElements.define('video-reels', VideoReels);
})();
